import { useState, useEffect, useCallback, useRef } from 'react'
import {
  initGame,
  canDeal, isEndGame,
  POINTS_PER_BONUS_CARD, type Card, type Grid, type DealTarget,
} from './game'
import { scoreGrid, type ScoringResult } from './scoring'
import { computeGravityStep, applyGravityStep, hasHoles } from './gravity'
import { rotateRowLeft, rotateRowRight, rotateColUp, rotateColDown } from './rotation'
import { PlayingCard } from './PlayingCard'
import { Rules } from './Rules'
import { applyBlokkerTick, tickStage3, type BlokkerTickResult } from './blokker'
import './App.css'

type ScoringPhase = 'idle' | 'rotating' | 'rows' | 'columns' | 'hold' | 'removing' | 'gravity' | 'gravity-animating' | 'dealing' | 'blokker-tick' | 'blokker-removing'

const ROTATE_DURATION = 180

interface ScorePopup {
  key: string
  text: string
  type: 'row' | 'col'
  doubled: boolean
  row?: number
  col?: number
}

function useCountUp(target: number, duration: number, active: boolean): number {
  const [displayed, setDisplayed] = useState(target)
  const startRef = useRef({ value: target, time: 0 })

  useEffect(() => {
    if (!active) {
      requestAnimationFrame(() => setDisplayed(target))
      return
    }
    startRef.current = { value: displayed, time: performance.now() }
    const from = startRef.current.value
    const animate = (now: number) => {
      const elapsed = now - startRef.current.time
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(from + (target - from) * eased))
      if (progress < 1) requestAnimationFrame(animate)
    }
    const id = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(id)
  }, [target, active]) // eslint-disable-line react-hooks/exhaustive-deps

  return displayed
}

function useCountUpOnIncrease(target: number, duration: number): number {
  const [displayed, setDisplayed] = useState(target)
  const fromRef = useRef(target)

  useEffect(() => {
    if (target <= fromRef.current) {
      fromRef.current = target
      requestAnimationFrame(() => setDisplayed(target))
      return
    }
    const from = fromRef.current
    const start = performance.now()
    let frameId = 0
    const animate = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const value = progress < 1 ? Math.round(from + (target - from) * eased) : target
      fromRef.current = value
      setDisplayed(value)
      if (progress < 1) frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [target, duration])

  return displayed
}

interface HighScore { score: number; date: string }

function loadHighScores(): HighScore[] {
  try { return JSON.parse(localStorage.getItem('blokker-highscores') || '[]') }
  catch { return [] }
}

function saveHighScore(score: number): { scores: HighScore[]; rank: number | null } {
  const scores = loadHighScores()
  const entry: HighScore = { score, date: new Date().toLocaleDateString() }
  scores.push(entry)
  scores.sort((a, b) => b.score - a.score)
  const topScores = scores.slice(0, 10)
  localStorage.setItem('blokker-highscores', JSON.stringify(topScores))
  const rank = topScores.findIndex(s => s === entry)
  return { scores: topScores, rank: rank >= 0 ? rank : null }
}

function GameOverOverlay({ score, onPlayAgain }: { score: number; onPlayAgain: () => void }) {
  const [result] = useState(() => saveHighScore(score))
  return (
    <div className="overlay">
      <div className="game-over-panel">
        <h2 className="game-over-title">Game Over</h2>
        <div className="game-over-score">{score}</div>
        <div className="game-over-label">Final Score</div>
        <div className="high-scores">
          <div className="high-scores-title">High Scores</div>
          {result.scores.map((entry, i) => (
            <div key={i} className={`high-score-entry ${i === result.rank ? 'high-score-current' : ''}`}>
              <span className="high-score-rank">{i + 1}.</span>
              <span className="high-score-value">{entry.score}</span>
              <span className="high-score-date">{entry.date}</span>
            </div>
          ))}
        </div>
        <button className="action-btn action-btn-primary" style={{ marginTop: 24 }} onClick={onPlayAgain}>Play Again</button>
      </div>
    </div>
  )
}

const ROW_DELAY = 200
const COL_DELAY = 200
const HOLD_DURATION = 1500
const REMOVE_DURATION = 300
const GRAVITY_STEP_DURATION = 250
const DEAL_DURATION = 300
const BLOKKER_TICK_DURATION = 700

function App() {
  const [initResult] = useState(() => initGame())
  const [gameState, setGameState] = useState(initResult.state)
  const rngRef = useRef(initResult.rng)
  const shoeRef = useRef(initResult.shoe)
  const blokkerRngRef = useRef(initResult.blokkerRng)
  const userActionPendingRef = useRef(false)
  const cycleScoredRef = useRef(false)

  const [scoringPhase, setScoringPhase] = useState<ScoringPhase>('idle')
  const [scoringResult, setScoringResult] = useState<ScoringResult | null>(null)
  const [visibleHighlights, setVisibleHighlights] = useState<Map<string, 'row' | 'col' | 'both'>>(new Map())
  const [revealedRows, setRevealedRows] = useState<Set<number>>(new Set())
  const [revealedCols, setRevealedCols] = useState<Set<number>>(new Set())
  const [removingCells, setRemovingCells] = useState<Set<string>>(new Set())
  const [fallingCells, setFallingCells] = useState<Map<string, number>>(new Map())
  const [scorePopups, setScorePopups] = useState<ScorePopup[]>([])
  const [pointsTarget, setPointsTarget] = useState(0)
  const [slidingCells, setSlidingCells] = useState<Map<string, 'left' | 'right' | 'up' | 'down'>>(new Map())
  const [comboMultiplier, setComboMultiplier] = useState(1)
  const [pendingBlokkerTick, setPendingBlokkerTick] = useState<BlokkerTickResult | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [creditsOpen, setCreditsOpen] = useState(false)
  const [rulesOpen, setRulesOpen] = useState(false)
  const [gameOver, setGameOver] = useState(false)

  const gridRef = useRef<HTMLDivElement>(null)

  const isAnimating = scoringPhase !== 'idle'
  const endGameVisible = !isAnimating && (isEndGame(gameState) || gameState.dealTarget.some(c => c !== null))
  const displayedPoints = useCountUp(pointsTarget, HOLD_DURATION * 0.8, scoringPhase === 'hold')
  const displayedCards = useCountUpOnIncrease(gameState.cardsRemaining, 600)

  // --- Callbacks ---

  const startScoring = useCallback((grid: Grid, dealTarget: DealTarget) => {
    const result = scoreGrid(grid)
    if (result.totalScore === 0) {
      if (hasHoles(grid, dealTarget)) {
        setScoringPhase('gravity')
        return
      }
      setComboMultiplier(1)

      const isUserActionTick = userActionPendingRef.current
      userActionPendingRef.current = false
      const scoredThisCycle = cycleScoredRef.current
      cycleScoredRef.current = false

      const tickTurns = isUserActionTick && !scoredThisCycle
      const rollSpawn = isUserActionTick && !scoredThisCycle

      const tick = applyBlokkerTick(grid, {
        totalPoints: gameState.points,
        tickTurns,
        rollSpawn,
      }, blokkerRngRef.current)
      const hasStage12 = grid.some(row => row.some(c => c?.blokker && c.blokker.stage !== 3))
      const tickHasEffect = tick.depleted.length > 0 || tick.spawned !== null || (tickTurns && hasStage12)

      if (!tickHasEffect) {
        setScoringPhase('idle')
        return
      }

      setGameState(prev => ({ ...prev, grid: tick.grid }))
      setPendingBlokkerTick(tick)
      setScoringPhase('blokker-tick')
      return
    }
    setScoringResult(result)
    setScoringPhase('rows')
    setRevealedRows(new Set())
    setRevealedCols(new Set())
    setVisibleHighlights(new Map())
  }, [gameState.points])

  const handleRowRotate = useCallback((row: number, direction: 'left' | 'right') => {
    if (isAnimating) return
    setGameState(prev => ({
      ...prev,
      grid: direction === 'left' ? rotateRowLeft(prev.grid, row) : rotateRowRight(prev.grid, row),
    }))
    const cells = new Map<string, 'left' | 'right' | 'up' | 'down'>()
    for (let c = 0; c < 5; c++) cells.set(`${row},${c}`, direction)
    setSlidingCells(cells)
    userActionPendingRef.current = true
    cycleScoredRef.current = false
    setScoringPhase('rotating')
  }, [isAnimating])

  const handleColRotate = useCallback((col: number, direction: 'up' | 'down') => {
    if (isAnimating) return
    setGameState(prev => ({
      ...prev,
      grid: direction === 'up' ? rotateColUp(prev.grid, col) : rotateColDown(prev.grid, col),
    }))
    const cells = new Map<string, 'left' | 'right' | 'up' | 'down'>()
    for (let r = 0; r < 5; r++) cells.set(`${r},${col}`, direction)
    setSlidingCells(cells)
    userActionPendingRef.current = true
    cycleScoredRef.current = false
    setScoringPhase('rotating')
  }, [isAnimating])

  const handleDeal = useCallback(() => {
    if (isAnimating || !canDeal(gameState)) return
    const shoe = shoeRef.current
    const dealTarget = [...gameState.nextHand] as DealTarget
    while (dealTarget.length < 5) dealTarget.push(null)
    const count = Math.min(5, gameState.cardsRemaining)
    const nextHand = count > 0 ? shoe.drawN(count) : []
    setGameState(prev => ({
      ...prev, dealTarget, nextHand,
      cardsRemaining: prev.cardsRemaining - count,
    }))
    userActionPendingRef.current = true
    cycleScoredRef.current = false
    setScoringPhase('dealing')
  }, [isAnimating, gameState])

  const handlePlayAgain = useCallback(() => {
    const result = initGame()
    rngRef.current = result.rng
    shoeRef.current = result.shoe
    blokkerRngRef.current = result.blokkerRng
    userActionPendingRef.current = false
    cycleScoredRef.current = false
    setPendingBlokkerTick(null)
    setGameState(result.state)
    setPointsTarget(0)
    setGameOver(false)
    setScoringPhase('idle')
  }, [])

  // --- Grid swipe (attach once, use refs for latest handlers) ---

  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    let sx = 0, sy = 0, sRow = -1, sCol = -1, tracking = false

    const onDown = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      sCol = Math.min(4, Math.max(0, Math.floor((e.clientX - rect.left) / (rect.width / 5))))
      sRow = Math.min(4, Math.max(0, Math.floor((e.clientY - rect.top) / (rect.height / 5))))
      sx = e.clientX; sy = e.clientY
      tracking = true
      el.setPointerCapture(e.pointerId)
    }
    const onUp = (e: PointerEvent) => {
      if (!tracking) return
      tracking = false
      const dx = e.clientX - sx, dy = e.clientY - sy
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= 30) {
        handleRowRotate(sRow, dx < 0 ? 'left' : 'right')
      } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) >= 30) {
        handleColRotate(sCol, dy < 0 ? 'up' : 'down')
      }
    }
    const onCancel = () => { tracking = false }

    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', onCancel)
    return () => {
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('pointercancel', onCancel)
    }
  }, [handleRowRotate, handleColRotate])

  // --- Scoring phase state machine ---

  useEffect(() => {
    if (scoringPhase === 'rotating') {
      const t = setTimeout(() => {
        setSlidingCells(new Map())
        setScoringPhase('gravity')
      }, ROTATE_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'dealing') {
      const t = setTimeout(() => setScoringPhase('gravity'), DEAL_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'rows') {
      if (!scoringResult) return
      const rows = scoringResult.rows.map((m, i) => m ? i : -1).filter(i => i >= 0)
      const next = rows.find(r => !revealedRows.has(r))
      if (next === undefined) { queueMicrotask(() => setScoringPhase('columns')); return }
      const t = setTimeout(() => {
        setRevealedRows(prev => new Set([...prev, next]))
        setVisibleHighlights(prev => {
          const m = new Map(prev)
          for (const c of scoringResult.rows[next]!.positions) m.set(`${next},${c}`, 'row')
          return m
        })
      }, ROW_DELAY)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'columns') {
      if (!scoringResult) return
      const cols = scoringResult.cols.map((m, i) => m ? i : -1).filter(i => i >= 0)
      const next = cols.find(c => !revealedCols.has(c))
      if (next === undefined) { queueMicrotask(() => setScoringPhase('hold')); return }
      const t = setTimeout(() => {
        setRevealedCols(prev => new Set([...prev, next]))
        setVisibleHighlights(prev => {
          const m = new Map(prev)
          for (const r of scoringResult.cols[next]!.positions) {
            const k = `${r},${next}`
            m.set(k, m.get(k) === 'row' ? 'both' : 'col')
          }
          return m
        })
      }, COL_DELAY)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'hold') {
      if (!scoringResult) return
      const mult = comboMultiplier
      const popups: ScorePopup[] = []
      scoringResult.rows.forEach((match, r) => {
        if (!match) return
        const doubled = scoringResult.doubledRows.has(r)
        const base = doubled ? match.score * 2 : match.score
        const display = base * mult
        const label = doubled ? `+${display} ×2 ${match.pattern}` : `+${display} ${match.pattern}`
        popups.push({
          key: `row-${r}`,
          text: mult > 1 ? `${label} (${mult}×)` : label,
          type: 'row', doubled, row: r,
        })
      })
      scoringResult.cols.forEach((match, c) => {
        if (!match) return
        const doubled = scoringResult.doubledCols.has(c)
        const base = doubled ? match.score * 2 : match.score
        const display = base * mult
        const label = doubled ? `+${display} ×2 ${match.pattern}` : `+${display} ${match.pattern}`
        popups.push({
          key: `col-${c}`,
          text: mult > 1 ? `${label} (${mult}×)` : label,
          type: 'col', doubled, col: c,
        })
      })
      const multipliedTotal = scoringResult.totalScore * mult
      queueMicrotask(() => {
        setScorePopups(popups)
        setPointsTarget(gameState.points + multipliedTotal)
      })
      const t = setTimeout(() => {
        setScorePopups([])
        setScoringPhase('removing')
      }, HOLD_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'removing') {
      if (!scoringResult) return
      const mult = comboMultiplier
      queueMicrotask(() => setRemovingCells(new Set(scoringResult.highlights.keys())))
      const t = setTimeout(() => {
        const multipliedTotal = scoringResult.totalScore * mult
        if (multipliedTotal > 0) cycleScoredRef.current = true
        setGameState(prev => {
          const cleared = prev.grid.map(r => [...r])
          for (const key of scoringResult.highlights.keys()) {
            const [r, c] = key.split(',').map(Number)
            cleared[r][c] = null
          }
          const newGrid = tickStage3(cleared, multipliedTotal)
          const shoe = shoeRef.current
          const bonusCardMeter = prev.pointsSinceLastBonusCard + multipliedTotal
          const bonusCards = Math.floor(bonusCardMeter / POINTS_PER_BONUS_CARD)
          let newRemaining = prev.cardsRemaining + bonusCards
          const newNextHand = [...prev.nextHand]
          const topUp = Math.min(5 - newNextHand.length, newRemaining)
          for (let i = 0; i < topUp; i++) {
            newNextHand.push(shoe.draw())
            newRemaining--
          }
          return {
            ...prev, grid: newGrid, nextHand: newNextHand,
            cardsRemaining: newRemaining,
            points: prev.points + multipliedTotal,
            pointsSinceLastBonusCard: bonusCardMeter % POINTS_PER_BONUS_CARD,
          }
        })
        setComboMultiplier(mult * 2)
        setScoringResult(null)
        setVisibleHighlights(new Map())
        setRemovingCells(new Set())
        setScoringPhase('gravity')
      }, REMOVE_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'gravity') {
      const moves = computeGravityStep(gameState.grid, gameState.dealTarget)
      if (moves.length === 0) {
        queueMicrotask(() => startScoring(gameState.grid, gameState.dealTarget))
        return
      }
      const { grid: newGrid, dealTarget: newDT } = applyGravityStep(gameState.grid, gameState.dealTarget, moves)
      const falling = new Map<string, number>()
      for (const m of moves) falling.set(`${m.toRow},${m.col}`, m.toRow - m.fromRow)
      queueMicrotask(() => {
        setGameState(prev => ({ ...prev, grid: newGrid, dealTarget: newDT }))
        setFallingCells(falling)
        setScoringPhase('gravity-animating')
      })
    }

    if (scoringPhase === 'gravity-animating') {
      const t = setTimeout(() => {
        setFallingCells(new Map())
        setScoringPhase('gravity')
      }, GRAVITY_STEP_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'blokker-tick') {
      const t = setTimeout(() => {
        if (pendingBlokkerTick && pendingBlokkerTick.depleted.length > 0) {
          const keys = new Set(pendingBlokkerTick.depleted.map(p => `${p.row},${p.col}`))
          setRemovingCells(keys)
          setScoringPhase('blokker-removing')
        } else {
          setPendingBlokkerTick(null)
          setScoringPhase('idle')
        }
      }, BLOKKER_TICK_DURATION)
      return () => clearTimeout(t)
    }

    if (scoringPhase === 'blokker-removing') {
      const depleted = pendingBlokkerTick?.depleted ?? []
      const t = setTimeout(() => {
        if (depleted.length > 0) {
          setGameState(prev => {
            const newGrid = prev.grid.map(row => row.slice())
            for (const pos of depleted) newGrid[pos.row][pos.col] = null
            return { ...prev, grid: newGrid }
          })
        }
        setRemovingCells(new Set())
        setPendingBlokkerTick(null)
        setScoringPhase('gravity')
      }, REMOVE_DURATION)
      return () => clearTimeout(t)
    }
  }, [scoringPhase, scoringResult, revealedRows, revealedCols, gameState.grid, gameState.dealTarget, gameState.points, comboMultiplier, startScoring, pendingBlokkerTick])

  // --- Render ---

  const { grid, dealTarget, nextHand } = gameState

  return (
    <div className="game-layout">
      {/* Score bar */}
      <div className="score-bar">
        <div>
          <div className={`stat-value ${scoringPhase === 'hold' ? 'points-counting' : ''}`}>{displayedPoints}</div>
          <div className="score-label">Points</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="stat-value">{displayedCards}</div>
          <div className="score-label">Cards</div>
        </div>
      </div>

      {/* Board section */}
      <div className="board-section">
        <div className="card-row deal-target-row">
            {dealTarget.map((card, c) => (
              <div key={c} className="card-slot">
                {card && (
                  <PlayingCard
                    key={`${card.suit}-${card.rank}`}
                    card={card}
                    className={scoringPhase === 'dealing' ? 'card-appearing' : undefined}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="col-arrows">
            {[0, 1, 2, 3, 4].map(c => (
              <button key={c} className="arrow-btn" disabled={isAnimating} onClick={() => handleColRotate(c, 'up')}>
                <svg viewBox="0 0 10 6" className="arrow-icon"><path d="M1 5L5 1L9 5" /></svg>
              </button>
            ))}
          </div>

          <div className="grid-body">
            <div className="row-arrows">
              {[0, 1, 2, 3, 4].map(r => (
                <button key={r} className="arrow-btn" disabled={isAnimating} onClick={() => handleRowRotate(r, 'left')}>
                  <svg viewBox="0 0 6 10" className="arrow-icon"><path d="M5 1L1 5L5 9" /></svg>
                </button>
              ))}
            </div>

            <div className="card-grid" ref={gridRef} style={{ touchAction: 'none' }}>
              {grid.flatMap((row, r) =>
                row.map((card, c) => {
                  const key = `${r},${c}`
                  const hl = visibleHighlights.get(key)
                  const tingeClass = hl === 'row' ? 'tinge-row' : hl === 'col' ? 'tinge-col' : hl === 'both' ? 'tinge-both' : ''
                  return (
                    <div key={key} className={`card-slot ${card ? tingeClass : ''}`}>
                      {card && (
                        <PlayingCard
                          key={`${card.suit}-${card.rank}`}
                          card={card}
                          className={[
                            removingCells.has(key) ? 'card-removing' : '',
                            fallingCells.has(key) ? 'card-falling' : '',
                            slidingCells.has(key) ? `card-slide-${slidingCells.get(key)}` : '',
                          ].filter(Boolean).join(' ') || undefined}
                          style={fallingCells.has(key) ? { '--fall-distance': fallingCells.get(key) } as React.CSSProperties : undefined}
                        />
                      )}
                    </div>
                  )
                }),
              )}
              {scorePopups.map(p => {
                const cls = p.doubled ? 'score-popup-doubled' : p.type === 'row' ? 'score-popup-row' : 'score-popup-col'
                const style: React.CSSProperties = p.type === 'row'
                  ? { top: `${p.row! * 20 + 10}%`, left: '50%', transform: 'translate(-50%,-50%)' }
                  : { left: `${p.col! * 20 + 10}%`, top: '50%', transform: 'translate(-50%,-50%)' }
                return <div key={p.key} className={`score-popup ${cls}`} style={style}>{p.text}</div>
              })}
            </div>

            <div className="row-arrows">
              {[0, 1, 2, 3, 4].map(r => (
                <button key={r} className="arrow-btn" disabled={isAnimating} onClick={() => handleRowRotate(r, 'right')}>
                  <svg viewBox="0 0 6 10" className="arrow-icon"><path d="M1 1L5 5L1 9" /></svg>
                </button>
              ))}
            </div>
          </div>

          <div className="col-arrows">
            {[0, 1, 2, 3, 4].map(c => (
              <button key={c} className="arrow-btn" disabled={isAnimating} onClick={() => handleColRotate(c, 'down')}>
                <svg viewBox="0 0 10 6" className="arrow-icon"><path d="M1 1L5 5L9 1" /></svg>
              </button>
            ))}
          </div>

          <div className="section-label">Next Hand</div>
          <div className="next-hand-body">
            <div className="card-row" onClick={handleDeal}>
              {Array.from({ length: 5 }, (_, i) => {
                const card = nextHand[i] as Card | undefined
                return (
                  <div key={i} className="card-slot">
                    {card && (
                      <PlayingCard key={`${card.suit}-${card.rank}`} card={card} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
      </div>

      {/* Action bar */}
      <div className="action-bar">
        {endGameVisible ? (
          <button className="action-btn action-btn-primary" onClick={() => setGameOver(true)}>End Game</button>
        ) : (
          <button className="action-btn action-btn-primary" disabled={isAnimating || !canDeal(gameState)} onClick={handleDeal}>Deal</button>
        )}
        <button className="action-btn action-btn-menu" onClick={() => setMenuOpen(true)}>
          <svg viewBox="0 0 20 14" className="menu-icon"><path d="M1 1h18M1 7h18M1 13h18" /></svg>
        </button>
      </div>

      {/* Menu */}
      {menuOpen && (
        <div className="overlay" onClick={() => setMenuOpen(false)}>
          <div className="menu-panel" onClick={e => e.stopPropagation()}>
            <button className="menu-item" onClick={() => { setMenuOpen(false); setGameOver(true) }}>Surrender</button>
            <button className="menu-item" onClick={() => { setMenuOpen(false); setRulesOpen(true) }}>Rules</button>
            <button className="menu-item" onClick={() => { setMenuOpen(false); setCreditsOpen(true) }}>Credits</button>
            <button className="menu-item" onClick={() => setMenuOpen(false)}>Resume</button>
          </div>
        </div>
      )}

      {/* Rules */}
      {rulesOpen && <Rules onBack={() => { setRulesOpen(false); setMenuOpen(true) }} />}

      {/* Credits */}
      {creditsOpen && (
        <div className="overlay" onClick={() => setCreditsOpen(false)}>
          <div className="menu-panel" onClick={e => e.stopPropagation()}>
            <p className="credits-body">
              Game by <a href="https://whois.cooling.coffee" target="_blank" rel="noopener noreferrer">Pawe&#322; J. Wal</a>.
            </p>
            <button className="menu-item" onClick={() => { setCreditsOpen(false); setMenuOpen(true) }}>Back</button>
          </div>
        </div>
      )}

      {/* Game over */}
      {gameOver && <GameOverOverlay score={gameState.points} onPlayAgain={handlePlayAgain} />}

    </div>
  )
}

export default App
